/**
 * Blur detection utility using Laplacian variance method.
 * Updated: downsamples to 480px wide before analysis to avoid
 * melting the CPU on high-resolution frames.
 */

const DETECT_MAX_WIDTH = 480;

/**
 * Downsample an image element to a smaller canvas for efficient analysis.
 */
function downsampleToCanvas(imageElement) {
  const srcW = imageElement.naturalWidth || imageElement.width;
  const srcH = imageElement.naturalHeight || imageElement.height;

  if (srcW <= DETECT_MAX_WIDTH) {
    // Already small enough — draw directly
    const canvas = document.createElement("canvas");
    canvas.width = srcW;
    canvas.height = srcH;
    canvas.getContext("2d").drawImage(imageElement, 0, 0);
    return canvas;
  }

  const scale = DETECT_MAX_WIDTH / srcW;
  const canvas = document.createElement("canvas");
  canvas.width = DETECT_MAX_WIDTH;
  canvas.height = Math.round(srcH * scale);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "medium";
  ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * Compute Laplacian variance as a sharpness metric.
 * Lower values = blurrier. Operates on a downsampled canvas.
 */
export const detectBlur = (imageElement) => {
  return new Promise((resolve) => {
    const canvas = downsampleToCanvas(imageElement);
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    const imageData = ctx.getImageData(0, 0, w, h);
    const pixels = imageData.data;

    // Laplacian kernel
    // [0, -1, 0]
    // [-1, 4, -1]
    // [0, -1, 0]

    let mean = 0;
    let count = 0;
    const laplacians = new Float32Array((w - 2) * (h - 2));

    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        // Grayscale of center and 4-neighbors
        const idx = (y * w + x) * 4;
        const center =
          (pixels[idx] + pixels[idx + 1] + pixels[idx + 2]) / 3;
        const top =
          (pixels[((y - 1) * w + x) * 4] +
            pixels[((y - 1) * w + x) * 4 + 1] +
            pixels[((y - 1) * w + x) * 4 + 2]) / 3;
        const bottom =
          (pixels[((y + 1) * w + x) * 4] +
            pixels[((y + 1) * w + x) * 4 + 1] +
            pixels[((y + 1) * w + x) * 4 + 2]) / 3;
        const left =
          (pixels[(y * w + x - 1) * 4] +
            pixels[(y * w + x - 1) * 4 + 1] +
            pixels[(y * w + x - 1) * 4 + 2]) / 3;
        const right =
          (pixels[(y * w + x + 1) * 4] +
            pixels[(y * w + x + 1) * 4 + 1] +
            pixels[(y * w + x + 1) * 4 + 2]) / 3;

        const lap = 4 * center - top - bottom - left - right;
        laplacians[count] = lap;
        mean += lap;
        count++;
      }
    }

    mean /= count;

    // Variance of Laplacian
    let variance = 0;
    for (let i = 0; i < count; i++) {
      variance += (laplacians[i] - mean) ** 2;
    }
    variance /= count;

    // Cleanup
    canvas.width = 0;
    canvas.height = 0;

    resolve(variance);
  });
};

/**
 * Analyze image blur and return a quality assessment.
 * @param {string} imageDataUrl  Base64 image data URL or Blob URL
 * @returns {Promise<{isBlurry: boolean, score: number, quality: string, message: string}>}
 */
export const analyzeImageBlur = (imageDataUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = async () => {
      try {
        const blurScore = await detectBlur(img);

        const BLUR_THRESHOLD_CRITICAL = 50;
        const BLUR_THRESHOLD_POOR = 150;
        const BLUR_THRESHOLD_FAIR = 500;

        let quality;
        if (blurScore < BLUR_THRESHOLD_CRITICAL) quality = "critical";
        else if (blurScore < BLUR_THRESHOLD_POOR) quality = "poor";
        else if (blurScore < BLUR_THRESHOLD_FAIR) quality = "fair";
        else quality = "good";

        const shouldWarn = quality === "critical" || quality === "poor";

        resolve({
          isBlurry: shouldWarn,
          score: blurScore,
          quality,
          message: shouldWarn
            ? quality === "critical"
              ? "Image is extremely blurry. Please retake for better grading accuracy."
              : "Image is quite blurry. Consider retaking for better results."
            : quality === "fair"
            ? "Image quality is acceptable."
            : "Image quality is excellent.",
        });
      } catch (error) {
        console.error("Error analyzing blur:", error);
        resolve({ isBlurry: false, score: 0, quality: "unknown", message: "Could not analyze image quality." });
      }
    };

    img.onerror = () => {
      resolve({ isBlurry: false, score: 0, quality: "unknown", message: "Could not load image for analysis." });
    };

    img.src = imageDataUrl;
  });
};
